import cx from 'classnames';
import React, { Component, PropsWithChildren, Fragment, KeyboardEvent } from 'react';
import { Button, Modal, ModalBody, ModalCloseBtn, ModalFooter, ModalHeader } from '../';
import { DataGridContext } from '../Grid/DataGrid/dataGridContext';
import { resolveProps } from '../utils/formikUtils';
import {
  IAssetFormat,
  IBaseSelectorProps,
  ISelectorAsset,
  ISelectorProps,
  ISelectorState,
  SelectorVerbosity,
} from './types';

import './Selector.scss';

export class BaseSelector extends Component<PropsWithChildren<IBaseSelectorProps>, ISelectorState> {
  public static getDerivedStateFromProps(newProps: ISelectorProps, state: ISelectorState) {
    if (
      !newProps.value.every((asset) => {
        return state.selectedMap.hasOwnProperty(asset.id) && state.selectedMap[asset.id].name === asset.name;
      })
    ) {
      return {
        ...state,
        selected: newProps.value,
        selectedMap: getSelectedMap(newProps.value),
      };
    }
    return null;
  }
  public readonly state: Readonly<ISelectorState> = {
    modalOpen: false,
    selected: this.props.value,
    selectedMap: getSelectedMap(this.props.value),
  };
  public render() {
    const {
      children,
      placeholder = 'Select Asset',
      modalTitle,
      multiSelect = false,
      required,
      value,
      assetFormat,
      valid = true,
      touched = false,
      title,
    } = this.props;
    const hasSelectedAssets = !!value.length;
    const linkDisplay = hasSelectedAssets ? this.formatAssetNames(value, assetFormat) : <span>{placeholder}</span>;

    const selectorHeader = (
      <DataGridContext.Consumer>
        {(renderProps) => {
          return (
            <div className="hlc-selector-header">
              {capitalize(assetFormat.multiple)}
              <div className="hlc-selector-actions">
                {`${this.state.selected.length} Selected`}
                <span>|</span>
                <Button
                  label={renderProps.viewSelected ? 'View All' : 'View Selections'}
                  color="link"
                  onClick={renderProps.toggleViewSelected}
                  type="button"
                />
                <span>|</span>
                <Button label="Clear" color="link" onClick={(e) => renderProps.deselectAll()} type="button" />
              </div>
            </div>
          );
        }}
      </DataGridContext.Consumer>
    );

    const clonedGrid = children
      ? React.cloneElement(children, {
          header: selectorHeader,
          initialState: {
            selected: value.map((asset) => asset.id),
            ...children.props.initialState,
          },
          multiSelect: multiSelect ? 'always' : 'never',
          onSelect: this.handleGridSelect,
        })
      : null;
    const showError = touched && !valid;

    const { error = '' } = resolveProps(this.props, this.props.formik);
    const errorMessage = typeof error === 'string' ? error : '';
    const selectorClasses = cx('hlc-selector', {
      'hlc-selector-invalid': !!errorMessage,
    });

    return (
      <Fragment>
        <a
          id={this.props.name}
          className={selectorClasses}
          tabIndex={0}
          role="button"
          title={errorMessage || title}
          onClick={this.openModal}
          onKeyDown={this.handleKeyPress}
          onBlur={this.handleBlur}
          qa-id={this.props['qa-id']}
        >
          {linkDisplay}
        </a>
        <span className="sr-only">{showError && '(error)'}</span>
        <Modal
          show={this.state.modalOpen}
          onHide={this.handleCancel}
          qa-id={`${this.props['qa-id']}-selector-modal`}
          size="large"
        >
          <ModalHeader title={modalTitle ? modalTitle : placeholder} closeButton={true} />
          <ModalBody>{clonedGrid}</ModalBody>
          <ModalFooter>
            <ModalCloseBtn />
            {/* TODO: do we want to configure the label for this? */}
            <Button color="primary" label="Select" onClick={this.handleChange} />
          </ModalFooter>
        </Modal>
      </Fragment>
    );
  }

  private formatAssetNames = (assets: ISelectorAsset[], assetFormat: IAssetFormat): string => {
    const { verbosity = SelectorVerbosity.SelectionCountOnly } = this.props;
    const isSingleAsset = assets.length === 1;
    const resolvedAssetFormat = isSingleAsset ? assetFormat.single : assetFormat.multiple;

    if (
      verbosity === SelectorVerbosity.SelectionCountOnly ||
      (verbosity === SelectorVerbosity.SingleNameMultiCount && !isSingleAsset)
    ) {
      return `${assets.length} ${resolvedAssetFormat}`;
    }

    return assets.map((asset) => asset.name).join(', ');
  };

  private openModal = () => {
    this.setState({ modalOpen: true });
  };

  private handleCancel = () => {
    const { value = [] } = this.props;
    // reset state selected to props selected
    this.setState({ selected: value, modalOpen: false });
  };

  private handleGridSelect = (selectedIds: any[], selectedValues: any[]) => {
    const { nameProperty = 'name' } = this.props;
    this.setState((currentState) => {
      return {
        selected: selectedValues.map((value) => {
          const currentlySelectedName = currentState.selectedMap[value.id]
            ? currentState.selectedMap[value.id].name
            : '';
          const name = value[nameProperty] ? value[nameProperty] : currentlySelectedName;
          return {
            id: value.id,
            name,
          };
        }),
      };
    });
  };

  private handleChange = () => {
    const { selected } = this.state;
    this.props.onChange(selected);
    this.setState({ modalOpen: false });
  };

  private handleKeyPress = (e: KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === 'Enter' || e.keyCode === 13 || e.which === 13) {
      this.openModal();
    }
  };

  private handleBlur = (e: any) => {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  };
}

function getSelectedMap(selected: ISelectorAsset[]): { [key: string]: ISelectorAsset } {
  return selected.reduce((map, asset) => {
    map[asset.id] = asset;
    return map;
  }, {});
}

function capitalize(str: string) {
  return `${str[0].toUpperCase()}${str.substr(1)}`;
}

export default BaseSelector;
